import { Course } from '../types/course';

export const courses: Course[] = [
  {
    id: 'cpc-initial',
    title: 'CPC Initial Qualification',
    description: 'Complete Driver Certificate of Professional Competence (CPC) initial qualification covering all essential areas for professional drivers.',
    category: 'cpc',
    level: 'beginner',
    duration: 2100, // 35 hours
    requiredHours: 35,
    isRequired: true,
    tags: ['CPC', 'Professional', 'Required', 'DVSA'],
    thumbnail: 'https://images.pexels.com/photos/1427541/pexels-photo-1427541.jpeg?auto=compress&cs=tinysrgb&w=400',
    certification: {
      type: 'Driver CPC',
      validityPeriod: 60, // 5 years
      authority: 'DVSA'
    },
    completionCriteria: {
      minimumScore: 80,
      attentionThreshold: 75,
      moduleCompletionRequired: true
    },
    modules: [
      {
        id: 'highway-code',
        title: 'Highway Code Fundamentals',
        description: 'Master the essential rules of the road, traffic signs, and road markings that every professional driver must know.',
        duration: 45,
        order: 1,
        learningObjectives: [
          'Identify and interpret all UK traffic signs and road markings',
          'Understand right of way rules and priority systems',
          'Apply speed limits correctly in different road conditions',
          'Recognize and respond to emergency vehicle signals'
        ],
        content: [
          {
            id: 'hc-intro',
            type: 'text',
            title: 'Introduction to the Highway Code',
            content: 'The Highway Code is the official guide to using roads safely and legally in Great Britain. As a professional driver, you have a higher responsibility to understand and follow these rules. The code covers rules for pedestrians, cyclists, motorcyclists, and drivers, with specific sections for different types of vehicles including large goods vehicles.',
            duration: 5,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'friendly'
              },
              keyTerms: ['Highway Code', 'Professional Driver', 'DVSA', 'Road Safety']
            }
          },
          {
            id: 'traffic-signs',
            type: 'interactive',
            title: 'Traffic Signs Recognition',
            content: 'Traffic signs are divided into three main categories: warning signs (triangular with red borders), regulatory signs (circular), and information signs (rectangular). Warning signs alert you to hazards ahead, such as sharp bends, steep hills, or road works. Regulatory signs give orders - red circles tell you what you must not do, blue circles tell you what you must do. Information signs provide guidance about routes, destinations, and services.',
            duration: 15,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'instructional'
              },
              interactionPoints: [300, 600, 900], // seconds where attention checks occur
              keyTerms: ['Warning Signs', 'Regulatory Signs', 'Information Signs', 'Traffic Management']
            }
          },
          {
            id: 'road-markings',
            type: 'text',
            title: 'Road Markings and Lane Discipline',
            content: 'Road markings work alongside traffic signs to provide information, warnings, and instructions. White lines separate traffic lanes and show the edge of the carriageway. Yellow lines indicate waiting and loading restrictions. Broken white lines mean you may cross or straddle them if safe to do so. Solid white lines mean you should not cross them unless entering or leaving a side road or property.',
            duration: 12,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'explanatory'
              },
              keyTerms: ['Road Markings', 'Lane Discipline', 'White Lines', 'Yellow Lines']
            }
          },
          {
            id: 'speed-limits',
            type: 'case_study',
            title: 'Speed Limits and Professional Responsibility',
            content: 'Speed limits are maximum speeds, not target speeds. Professional drivers must adjust their speed according to road, traffic, and weather conditions. For HGVs over 7.5 tonnes, the speed limit is 50 mph on single carriageways, 60 mph on dual carriageways, and 70 mph on motorways. However, many companies set lower maximum speeds for safety and fuel efficiency. Remember, you can be prosecuted for driving too slowly if it causes danger or inconvenience to other road users.',
            duration: 13,
            order: 4,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.88,
                pitch: 'medium',
                style: 'authoritative'
              },
              keyTerms: ['Speed Limits', 'HGV Limits', 'Professional Responsibility', 'Road Conditions']
            }
          }
        ],
        assessments: [
          {
            id: 'hc-quiz',
            type: 'quiz',
            title: 'Highway Code Knowledge Check',
            passingScore: 80,
            timeLimit: 15,
            attempts: 3,
            questions: [
              {
                id: 'hc-q1',
                type: 'multiple_choice',
                question: 'What is the maximum speed limit for an HGV over 7.5 tonnes on a dual carriageway?',
                options: ['50 mph', '60 mph', '70 mph', '80 mph'],
                correctAnswer: 1,
                explanation: 'HGVs over 7.5 tonnes are limited to 60 mph on dual carriageways, regardless of the posted speed limit for cars.',
                points: 5,
                difficulty: 'medium',
                category: 'Speed Limits'
              },
              {
                id: 'hc-q2',
                type: 'multiple_choice',
                question: 'What does a red triangle warning sign indicate?',
                options: ['Speed limit ahead', 'No entry', 'Danger or hazard ahead', 'Mandatory instruction'],
                correctAnswer: 2,
                explanation: 'Red triangle signs are warning signs that alert drivers to potential dangers or hazards ahead on the road.',
                points: 5,
                difficulty: 'easy',
                category: 'Traffic Signs'
              }
            ]
          }
        ],
        resources: [
          {
            id: 'hc-official',
            title: 'Official Highway Code',
            type: 'link',
            url: 'https://www.gov.uk/highway-code',
            description: 'The complete official Highway Code from GOV.UK',
            downloadable: false
          }
        ]
      },
      {
        id: 'vehicle-safety',
        title: 'Vehicle Safety Checks and Maintenance',
        description: 'Learn comprehensive pre-journey inspection procedures and ongoing vehicle maintenance responsibilities.',
        duration: 30,
        order: 2,
        learningObjectives: [
          'Perform thorough daily vehicle safety checks',
          'Identify potential mechanical issues before they become dangerous',
          'Understand legal requirements for vehicle maintenance',
          'Complete accurate defect reporting procedures'
        ],
        content: [
          {
            id: 'daily-checks',
            type: 'text',
            title: 'Daily Vehicle Inspection Routine',
            content: 'Every professional driver must conduct a thorough vehicle inspection before starting their journey. This is not just good practice - it is a legal requirement. Your daily check should include examining tyres for wear and damage, checking all lights and indicators work correctly, ensuring mirrors are clean and properly adjusted, verifying that the handbrake operates effectively, and confirming that the steering responds correctly without excessive play.',
            duration: 8,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'instructional'
              },
              keyTerms: ['Daily Inspection', 'Legal Requirement', 'Vehicle Safety', 'Pre-journey Check']
            }
          },
          {
            id: 'tyre-safety',
            type: 'interactive',
            title: 'Tyre Safety and Legal Requirements',
            content: 'Tyres are your only contact with the road, making them critical for safety. Check tyre pressure when tyres are cold, as heat from driving increases pressure. Look for cuts, bulges, or objects embedded in the tyre. The legal minimum tread depth is 1.6mm across the central three-quarters of the tyre width. For commercial vehicles, consider that worn tyres increase stopping distances dramatically, especially in wet conditions. Replace tyres before they reach the legal limit for optimal safety.',
            duration: 12,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'detailed'
              },
              interactionPoints: [360, 600],
              keyTerms: ['Tyre Safety', 'Tread Depth', 'Tyre Pressure', 'Legal Requirements']
            }
          },
          {
            id: 'defect-reporting',
            type: 'text',
            title: 'Defect Reporting and Documentation',
            content: 'When you discover a defect during your vehicle check, you must report it immediately to your transport manager or designated person. Document the defect clearly, including its location, severity, and potential impact on safety. Some defects may prevent the vehicle from being used until repaired, while others may be noted for attention at the next service. Never ignore a defect, even if it seems minor - small problems can quickly become major safety hazards.',
            duration: 10,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.88,
                pitch: 'medium',
                style: 'serious'
              },
              keyTerms: ['Defect Reporting', 'Documentation', 'Transport Manager', 'Safety Hazards']
            }
          }
        ],
        assessments: [
          {
            id: 'safety-practical',
            type: 'practical',
            title: 'Vehicle Safety Check Demonstration',
            passingScore: 85,
            timeLimit: 20,
            attempts: 2,
            questions: [
              {
                id: 'safety-q1',
                type: 'scenario',
                question: 'During your daily check, you notice a small cut in the sidewall of a front tyre. What should you do?',
                options: [
                  'Continue driving but monitor the tyre carefully',
                  'Report the defect and do not use the vehicle',
                  'Only drive short distances until it can be repaired',
                  'Check if the cut goes through to the inner structure'
                ],
                correctAnswer: 1,
                explanation: 'Any damage to a tyre sidewall is potentially dangerous and the vehicle should not be used until the tyre is inspected by a qualified person.',
                points: 10,
                difficulty: 'medium',
                category: 'Safety Procedures'
              }
            ]
          }
        ],
        resources: [
          {
            id: 'vehicle-check-guide',
            title: 'Daily Vehicle Check Guide',
            type: 'pdf',
            url: '/resources/daily-vehicle-check-guide.pdf',
            description: 'Comprehensive checklist for daily vehicle inspections',
            downloadable: true
          }
        ]
      },
      {
        id: 'defensive-driving',
        title: 'Defensive Driving Techniques',
        description: 'Master advanced defensive driving strategies to anticipate and avoid potential hazards on the road.',
        duration: 60,
        order: 3,
        learningObjectives: [
          'Apply the MSPSL routine consistently',
          'Maintain appropriate following distances in all conditions',
          'Recognize and respond to potential hazards early',
          'Use defensive positioning to maximize safety margins'
        ],
        content: [
          {
            id: 'mspsl-routine',
            type: 'text',
            title: 'The MSPSL Routine',
            content: 'MSPSL stands for Mirrors, Signal, Position, Speed, Look. This routine should become second nature for every manoeuvre. Mirrors: Check your mirrors to assess the situation behind and alongside. Signal: Give clear, timely signals to communicate your intentions. Position: Move to the correct position for your intended path. Speed: Adjust your speed appropriately for the manoeuvre and conditions. Look: Make final observations before completing the manoeuvre. This systematic approach helps ensure you make safe, well-planned decisions.',
            duration: 15,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'methodical'
              },
              keyTerms: ['MSPSL', 'Mirrors', 'Signal', 'Position', 'Speed', 'Look']
            }
          },
          {
            id: 'following-distance',
            type: 'interactive',
            title: 'Safe Following Distances',
            content: 'The two-second rule is a minimum in good conditions, but professional drivers should use longer following distances. In wet conditions, double your following distance. In icy conditions, increase it to ten times the normal distance. For vehicles over 7.5 tonnes, consider using a three-second minimum even in good conditions. Remember, the faster you travel, the longer it takes to stop. Use fixed reference points to measure your following distance - when the vehicle ahead passes a landmark, count the seconds until you reach the same point.',
            duration: 20,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'explanatory'
              },
              interactionPoints: [480, 900, 1200],
              keyTerms: ['Following Distance', 'Two-Second Rule', 'Stopping Distance', 'Weather Conditions']
            }
          },
          {
            id: 'hazard-perception',
            type: 'simulation',
            title: 'Hazard Recognition and Response',
            content: 'Effective hazard perception involves constantly scanning the road environment and identifying potential dangers before they develop into actual hazards. Look for pedestrians who might step into the road, vehicles that might change lanes suddenly, or road conditions that could affect your vehicle control. The key is to spot these potential hazards early and take preventive action. This might mean adjusting your speed, changing position, or increasing your following distance. Always have an escape route planned.',
            duration: 25,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.88,
                pitch: 'medium',
                style: 'alert'
              },
              interactionPoints: [600, 1200],
              keyTerms: ['Hazard Perception', 'Road Scanning', 'Preventive Action', 'Escape Route']
            }
          }
        ],
        assessments: [
          {
            id: 'defensive-scenario',
            type: 'scenario',
            title: 'Defensive Driving Scenarios',
            passingScore: 75,
            timeLimit: 25,
            attempts: 3,
            questions: [
              {
                id: 'def-q1',
                type: 'scenario',
                question: 'You are approaching a roundabout in heavy traffic. A car in the left lane appears to be positioning to go straight ahead, but their right indicator is on. What should you do?',
                options: [
                  'Assume they will go straight and proceed normally',
                  'Follow their indicator signal and expect them to turn right',
                  'Increase following distance and be prepared for either action',
                  'Sound your horn to alert them to the conflicting signals'
                ],
                correctAnswer: 2,
                explanation: 'When signals and positioning conflict, you should prepare for either possibility and maintain extra safety margins.',
                points: 8,
                difficulty: 'hard',
                category: 'Hazard Response',
                media: {
                  type: 'image',
                  url: 'https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=400',
                  description: 'Roundabout approach scenario'
                }
              }
            ]
          }
        ],
        resources: [
          {
            id: 'defensive-driving-manual',
            title: 'Advanced Defensive Driving Manual',
            type: 'pdf',
            url: '/resources/defensive-driving-manual.pdf',
            description: 'Comprehensive guide to defensive driving techniques',
            downloadable: true
          }
        ]
      },
      {
        id: 'emergency-procedures',
        title: 'Emergency Procedures and Breakdown Response',
        description: 'Learn proper procedures for handling emergencies, breakdowns, and unexpected situations on the road.',
        duration: 40,
        order: 4,
        learningObjectives: [
          'Execute proper breakdown procedures on different road types',
          'Respond appropriately to various emergency situations',
          'Use emergency equipment correctly and safely',
          'Communicate effectively with emergency services'
        ],
        content: [
          {
            id: 'breakdown-procedure',
            type: 'text',
            title: 'Breakdown Procedures',
            content: 'If your vehicle breaks down, your priority is safety. On a motorway, try to reach the hard shoulder or an emergency refuge area. Switch on hazard warning lights immediately. If possible, leave the vehicle by the nearside door and wait away from the carriageway. Place a warning triangle 45 metres behind your vehicle on normal roads, but never use one on a motorway. Call for assistance and give your exact location. If you cannot reach the hard shoulder, stay in your vehicle with seatbelt on and call 999.',
            duration: 12,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'urgent'
              },
              keyTerms: ['Breakdown', 'Hard Shoulder', 'Hazard Lights', 'Warning Triangle', 'Emergency Services']
            }
          },
          {
            id: 'fire-procedures',
            type: 'interactive',
            title: 'Fire Emergency Procedures',
            content: 'Vehicle fires can develop rapidly and are extremely dangerous. If you suspect a fire, stop immediately in a safe location away from other vehicles and buildings. Switch off the engine and disconnect the battery if possible. Get everyone away from the vehicle - at least 100 metres for large vehicles. Call the fire brigade immediately. Only attempt to fight small fires with appropriate extinguishers if you are trained and it is safe to do so. Never open the bonnet fully if you suspect an engine fire - this can cause the fire to flare up dramatically.',
            duration: 15,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'serious'
              },
              interactionPoints: [450, 750],
              keyTerms: ['Vehicle Fire', 'Fire Extinguisher', 'Fire Brigade', 'Safety Distance']
            }
          },
          {
            id: 'accident-response',
            type: 'case_study',
            title: 'Road Traffic Accident Response',
            content: 'At the scene of an accident, your first priority is safety. Stop in a safe position and switch on hazard lights. Check for injuries and call emergency services if needed. Do not move injured people unless they are in immediate danger. Make the scene safe by directing traffic if necessary, but be very careful of your own safety. Exchange insurance details with other drivers involved. Take photos of vehicle positions and damage if safe to do so. Report the accident to your employer as soon as possible.',
            duration: 13,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.88,
                pitch: 'medium',
                style: 'calm'
              },
              keyTerms: ['Road Accident', 'Emergency Services', 'Insurance Details', 'Scene Safety']
            }
          }
        ],
        assessments: [
          {
            id: 'emergency-quiz',
            type: 'quiz',
            title: 'Emergency Procedures Assessment',
            passingScore: 85,
            timeLimit: 20,
            attempts: 2,
            questions: [
              {
                id: 'emerg-q1',
                type: 'multiple_choice',
                question: 'Your vehicle breaks down on a motorway. You manage to stop on the hard shoulder. What should you do first?',
                options: [
                  'Place a warning triangle behind the vehicle',
                  'Switch on hazard warning lights',
                  'Call for breakdown assistance',
                  'Check what is wrong with the vehicle'
                ],
                correctAnswer: 1,
                explanation: 'Hazard warning lights should be switched on immediately to warn other traffic of your presence.',
                points: 10,
                difficulty: 'medium',
                category: 'Breakdown Procedures'
              }
            ]
          }
        ],
        resources: [
          {
            id: 'emergency-contact-card',
            title: 'Emergency Contact Card',
            type: 'pdf',
            url: '/resources/emergency-contact-card.pdf',
            description: 'Essential emergency contact numbers and procedures',
            downloadable: true
          }
        ]
      },
      {
        id: 'professional-standards',
        title: 'Professional Driving Standards and Customer Service',
        description: 'Understand the professional standards expected of commercial drivers and how to provide excellent customer service.',
        duration: 55,
        order: 5,
        learningObjectives: [
          'Demonstrate professional behavior and appearance standards',
          'Provide excellent customer service in all interactions',
          'Handle difficult situations with professionalism',
          'Understand the impact of driver behavior on company reputation'
        ],
        content: [
          {
            id: 'professional-image',
            type: 'text',
            title: 'Professional Image and Behavior',
            content: 'As a professional driver, you represent your company every time you are on the road. Your appearance, behavior, and driving style all contribute to your company\'s reputation. Maintain a clean, professional appearance and ensure your vehicle is clean and well-maintained. Be courteous to other road users, even when they make mistakes. Avoid aggressive driving behaviors such as tailgating, excessive speed, or inappropriate use of the horn. Remember that your vehicle likely displays your company name and contact details - poor driving reflects badly on your employer.',
            duration: 15,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'professional'
              },
              keyTerms: ['Professional Image', 'Company Reputation', 'Courteous Driving', 'Professional Appearance']
            }
          },
          {
            id: 'customer-service',
            type: 'interactive',
            title: 'Customer Service Excellence',
            content: 'Excellent customer service starts with a positive attitude and professional approach. Greet customers politely and introduce yourself. Listen carefully to their needs and ask questions if you need clarification. Keep customers informed about delivery times and any potential delays. Handle complaints calmly and professionally - apologize for any inconvenience and focus on finding solutions. If you cannot resolve an issue, escalate it to your supervisor promptly. Remember that satisfied customers are more likely to use your company\'s services again.',
            duration: 20,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.88,
                pitch: 'medium',
                style: 'friendly'
              },
              interactionPoints: [600, 1080],
              keyTerms: ['Customer Service', 'Professional Approach', 'Complaint Handling', 'Customer Satisfaction']
            }
          },
          {
            id: 'difficult-situations',
            type: 'case_study',
            title: 'Handling Difficult Situations',
            content: 'Difficult situations are inevitable in professional driving. You might encounter aggressive customers, delivery problems, or challenging road conditions. The key is to remain calm and professional at all times. If a customer becomes aggressive or abusive, do not respond in kind. Stay polite but firm, and remove yourself from the situation if necessary. Document any incidents and report them to your supervisor. If you encounter problems with deliveries, communicate proactively with both the customer and your company to find solutions.',
            duration: 20,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'calm'
              },
              keyTerms: ['Difficult Situations', 'Conflict Resolution', 'Professional Response', 'Incident Documentation']
            }
          }
        ],
        assessments: [
          {
            id: 'professional-scenario',
            type: 'scenario',
            title: 'Professional Standards Scenarios',
            passingScore: 80,
            timeLimit: 30,
            attempts: 3,
            questions: [
              {
                id: 'prof-q1',
                type: 'scenario',
                question: 'A customer is angry because their delivery is late due to traffic delays. They are shouting and using offensive language. How should you respond?',
                options: [
                  'Explain that the delay is not your fault and they should complain to your company',
                  'Apologize for the delay, explain the situation calmly, and offer to contact your office',
                  'Tell them that shouting will not make the delivery arrive any faster',
                  'Leave immediately and report the customer as abusive'
                ],
                correctAnswer: 1,
                explanation: 'Professional customer service involves acknowledging the customer\'s frustration, apologizing for the inconvenience, and taking positive action to help resolve the situation.',
                points: 10,
                difficulty: 'medium',
                category: 'Customer Service'
              }
            ]
          }
        ],
        resources: [
          {
            id: 'customer-service-guide',
            title: 'Customer Service Best Practices',
            type: 'document',
            url: '/resources/customer-service-guide.pdf',
            description: 'Comprehensive guide to professional customer service',
            downloadable: true
          }
        ]
      }
    ],
    prerequisites: [],
  },
  {
    id: 'cpc-periodic',
    title: 'CPC Periodic Training',
    description: 'Annual 7-hour refresher training to maintain your Driver CPC qualification and stay updated with latest regulations.',
    category: 'cpc',
    level: 'intermediate',
    duration: 420, // 7 hours
    requiredHours: 7,
    isRequired: true,
    tags: ['CPC', 'Periodic', 'Refresher', 'Annual'],
    thumbnail: 'https://images.pexels.com/photos/1427541/pexels-photo-1427541.jpeg?auto=compress&cs=tinysrgb&w=400',
    prerequisites: ['cpc-initial'],
    certification: {
      type: 'CPC Periodic Training Certificate',
      validityPeriod: 12,
      authority: 'DVSA'
    },
    completionCriteria: {
      minimumScore: 75,
      attentionThreshold: 70,
      moduleCompletionRequired: true
    },
    modules: [
      {
        id: 'regulation-updates',
        title: 'Latest Regulation Updates',
        description: 'Stay current with the latest changes in transport regulations and legislation.',
        duration: 105, // 1.75 hours
        order: 1,
        learningObjectives: [
          'Understand recent changes to driving hours regulations',
          'Apply updated vehicle safety requirements',
          'Comply with new environmental standards',
          'Implement latest loading and securing requirements'
        ],
        content: [
          {
            id: 'hours-regulations',
            type: 'text',
            title: 'Driving Hours and Rest Periods Updates',
            content: 'Recent updates to EU and UK driving hours regulations have introduced new flexibility measures while maintaining safety standards. The emergency relaxation measures introduced during the pandemic have been reviewed and some made permanent. Key changes include modified break requirements for certain operations and updated rules for multi-manning. It\'s crucial to understand these changes to ensure compliance and avoid penalties.',
            duration: 25,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'informative'
              },
              keyTerms: ['Driving Hours', 'Rest Periods', 'EU Regulations', 'Compliance']
            }
          },
          {
            id: 'environmental-standards',
            type: 'interactive',
            title: 'Environmental Standards and Clean Air Zones',
            content: 'Environmental regulations are becoming increasingly important for commercial vehicles. Clean Air Zones (CAZ) have been introduced in major cities, with charges for non-compliant vehicles. Euro VI emission standards are now mandatory for new vehicles, and older vehicles may face restrictions or charges. Low Emission Zones (LEZ) operate in London and other cities. Understanding these requirements is essential for route planning and cost management.',
            duration: 30,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.88,
                pitch: 'medium',
                style: 'explanatory'
              },
              interactionPoints: [900, 1500],
              keyTerms: ['Clean Air Zones', 'Euro VI', 'Low Emission Zones', 'Environmental Compliance']
            }
          },
          {
            id: 'safety-updates',
            type: 'text',
            title: 'Vehicle Safety Technology Updates',
            content: 'New safety technologies are becoming mandatory on commercial vehicles. Advanced Emergency Braking Systems (AEBS) are now required on new HGVs. Lane Departure Warning Systems (LDWS) help prevent accidents caused by unintentional lane changes. Intelligent Speed Assistance (ISA) systems help drivers comply with speed limits. Understanding how these systems work and their limitations is crucial for safe operation.',
            duration: 25,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'technical'
              },
              keyTerms: ['AEBS', 'LDWS', 'ISA', 'Safety Technology']
            }
          },
          {
            id: 'loading-securing',
            type: 'case_study',
            title: 'Load Securing Regulation Updates',
            content: 'Load securing regulations have been updated to reflect new materials and securing methods. The use of load restraint systems must now meet enhanced standards. Regular inspections of securing equipment are mandatory, with detailed record-keeping requirements. Penalties for inadequately secured loads have increased significantly. Understanding proper load distribution and securing techniques is essential for legal compliance and road safety.',
            duration: 25,
            order: 4,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'detailed'
              },
              keyTerms: ['Load Securing', 'Restraint Systems', 'Load Distribution', 'Compliance Records']
            }
          }
        ],
        assessments: [
          {
            id: 'regulations-quiz',
            type: 'quiz',
            title: 'Regulation Updates Assessment',
            passingScore: 75,
            timeLimit: 20,
            attempts: 3,
            questions: [
              {
                id: 'reg-q1',
                type: 'multiple_choice',
                question: 'What is the main purpose of Clean Air Zones (CAZ)?',
                options: [
                  'To reduce traffic congestion in city centers',
                  'To improve air quality by restricting polluting vehicles',
                  'To generate revenue for local authorities',
                  'To promote the use of public transport'
                ],
                correctAnswer: 1,
                explanation: 'Clean Air Zones are designed to improve air quality by charging or restricting the most polluting vehicles in areas with poor air quality.',
                points: 5,
                difficulty: 'medium',
                category: 'Environmental Regulations'
              }
            ]
          }
        ],
        resources: [
          {
            id: 'regulation-summary',
            title: 'Latest Regulation Summary',
            type: 'pdf',
            url: '/resources/regulation-updates-summary.pdf',
            description: 'Summary of all recent regulation changes',
            downloadable: true
          }
        ]
      }
    ]
  },
  {
    id: 'hazard-perception',
    title: 'Advanced Hazard Perception Training',
    description: 'Develop advanced hazard awareness and response skills through interactive scenarios and real-world case studies.',
    category: 'hazard',
    level: 'advanced',
    duration: 210, // 3.5 hours
    requiredHours: 3.5,
    isRequired: false,
    tags: ['Hazard Perception', 'Advanced', 'Safety', 'Interactive'],
    thumbnail: 'https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=400',
    prerequisites: ['cpc-initial'],
    completionCriteria: {
      minimumScore: 85,
      attentionThreshold: 80,
      moduleCompletionRequired: true
    },
    modules: [
      {
        id: 'hazard-identification',
        title: 'Advanced Hazard Identification',
        description: 'Learn to identify potential hazards before they become actual dangers.',
        duration: 70,
        order: 1,
        learningObjectives: [
          'Identify static and dynamic hazards effectively',
          'Assess risk levels of different hazard types',
          'Develop systematic scanning techniques',
          'Understand hazard development patterns'
        ],
        content: [
          {
            id: 'scanning-techniques',
            type: 'interactive',
            title: 'Systematic Road Scanning',
            content: 'Effective hazard perception requires systematic visual scanning of the road environment. Use the 12-second rule - constantly scan the area you will reach in the next 12 seconds. Check mirrors every 5-8 seconds. Look for movement, unusual positioning, or anything that seems out of place. Pay special attention to junctions, pedestrian crossings, and areas where visibility is restricted. Develop a mental commentary of what you see and what might happen next.',
            duration: 25,
            order: 1,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.85,
                pitch: 'medium',
                style: 'instructional'
              },
              interactionPoints: [750, 1200],
              keyTerms: ['Visual Scanning', '12-Second Rule', 'Mirror Checks', 'Mental Commentary']
            }
          },
          {
            id: 'dynamic-hazards',
            type: 'simulation',
            title: 'Dynamic Hazard Recognition',
            content: 'Dynamic hazards are moving elements that could become dangerous. These include pedestrians who might step into the road, cyclists who might swerve, or vehicles that might change lanes suddenly. Watch for early warning signs: a pedestrian looking at their phone, a cyclist wobbling, or a car with its indicator on. The key is to spot these potential hazards early and adjust your driving accordingly, rather than waiting for them to become actual dangers.',
            duration: 30,
            order: 2,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'alert'
              },
              interactionPoints: [900, 1500],
              keyTerms: ['Dynamic Hazards', 'Early Warning Signs', 'Pedestrian Behavior', 'Vehicle Movements']
            }
          },
          {
            id: 'static-hazards',
            type: 'text',
            title: 'Static Hazard Assessment',
            content: 'Static hazards are fixed features that create potential dangers. These include sharp bends, steep hills, narrow bridges, or parked vehicles. While these hazards don\'t move, the risk they present can change based on conditions. A parked car becomes more hazardous if someone is sitting in it, a bend becomes more dangerous in wet weather, and a hill presents different challenges when loaded versus empty. Always assess how static hazards might affect your journey.',
            duration: 15,
            order: 3,
            metadata: {
              voiceSettings: {
                voice: 'en-GB-RyanNeural',
                speed: 0.9,
                pitch: 'medium',
                style: 'analytical'
              },
              keyTerms: ['Static Hazards', 'Fixed Features', 'Risk Assessment', 'Conditional Dangers']
            }
          }
        ],
        assessments: [
          {
            id: 'hazard-id-test',
            type: 'scenario',
            title: 'Hazard Identification Test',
            passingScore: 85,
            timeLimit: 15,
            attempts: 2,
            questions: [
              {
                id: 'haz-q1',
                type: 'scenario',
                question: 'You are approaching a school during home time. You notice children on the pavement and a lollipop person at the crossing ahead. What potential hazards should you be most aware of?',
                options: [
                  'Only the lollipop person stepping into the road',
                  'Children running between parked cars and the lollipop person\'s signals',
                  'Other drivers not stopping for the crossing',
                  'The school bus pulling out from the school entrance'
                ],
                correctAnswer: 1,
                explanation: 'Near schools, you must watch for multiple hazards: children who might run into the road unexpectedly and the lollipop person who has authority to stop traffic.',
                points: 10,
                difficulty: 'hard',
                category: 'Hazard Recognition'
              }
            ]
          }
        ],
        resources: [
          {
            id: 'hazard-guide',
            title: 'Hazard Perception Guide',
            type: 'pdf',
            url: '/resources/hazard-perception-guide.pdf',
            description: 'Comprehensive guide to hazard identification and assessment',
            downloadable: true
          }
        ]
      }
    ]
  }
];